import { AxiosResponse } from 'axios';
import { BULLMQ_JOBS } from '../../constant/message-queue.constant';
import { IUploadFileResponse } from '../../interface/bulkUpload.interface';
import api from '../api';
import apiMultipart from '../api-multipart';
import { BULK_UPLOAD_CONFIG } from './config';

/**
 * Download bulk upload template for specified module
 * Triggers browser download with configured filename
 *
 * @param moduleKey - Module identifier from BULLMQ_JOBS enum
 * @throws Error if module configuration not found or download fails
 */
const downloadTemplate = async (moduleKey: BULLMQ_JOBS): Promise<void> => {
  const config = BULK_UPLOAD_CONFIG[moduleKey];

  if (!config) {
    throw new Error(`Bulk upload configuration not found for module: ${moduleKey}`);
  }

  const response: AxiosResponse<Blob> = await api.get(config.downloadUrl, {
    responseType: 'blob',
  });

  // Trigger browser download
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', config.templateFileName);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Upload bulk data file for specified module
 * Uses multipart/form-data for file upload
 *
 * @param moduleKey - Module identifier from BULLMQ_JOBS enum
 * @param formData - FormData containing the Excel file
 * @returns Promise<AxiosResponse<IUploadFileResponse>> - Upload response
 * @throws Error if module configuration not found or upload fails
 */
const uploadFile = async (
  moduleKey: BULLMQ_JOBS,
  formData: FormData,
): Promise<AxiosResponse<IUploadFileResponse>> => {
  const config = BULK_UPLOAD_CONFIG[moduleKey];

  if (!config) {
    throw new Error(`Bulk upload configuration not found for module: ${moduleKey}`);
  }

  const response: AxiosResponse<IUploadFileResponse> = await apiMultipart.post(
    config.uploadUrl,
    formData,
  );

  return response;
};

/**
 * Bulk Upload Service
 * Centralized service for all bulk upload operations
 * Exports service object with downloadTemplate and uploadFile methods
 */
export const bulkUploadService = {
  downloadTemplate,
  uploadFile,
};
